import React, { useEffect, useState } from "react";
import { fopSettingsApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Building2,
  CreditCard,
  ArrowUp,
  ArrowDown,
  Globe,
  Banknote,
  Upload,
  FileSignature,
  X,
} from "lucide-react";

const COMPANY_TYPES = [
  { value: "fop", label: "FOP (ФОП)" },
  { value: "llc", label: "LLC (ТОВ)" },
  { value: "corp", label: "Corporation" },
];

const CURRENCIES = ["UAH", "USD", "EUR", "GBP", "PLN"];

const FopSettingsPage = () => {
  const [fops, setFops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFop, setEditingFop] = useState(null);
  const [saving, setSaving] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [form, setForm] = useState({
    name: "",
    full_name: "",
    company_type: "fop",
    country: "Ukraine",
    address: "",
    edrpou: "",
    bank_name: "",
    swift: "",
    bank_accounts: [],
    privatbank_token: "",
    is_active: true,
    phone: "",
    signer: "",
    signer_gen: "",
  });

  useEffect(() => {
    fetchFops();
  }, []);

  const fetchFops = async () => {
    try {
      const res = await fopSettingsApi.getAll();
      setFops(res.data || []);
    } catch (err) {
      toast.error("Failed to load legal entities");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (fop = null) => {
    if (fop) {
      setEditingFop(fop);
      setForm({
        name: fop.name || "",
        full_name: fop.full_name || "",
        company_type: fop.company_type || "fop",
        country: fop.country || "Ukraine",
        address: fop.address || "",
        edrpou: fop.edrpou || "",
        bank_name: fop.bank_name || "",
        swift: fop.swift || "",
        bank_accounts: fop.bank_accounts || [],
        privatbank_token: fop.privatbank_token || "",
        is_active: fop.is_active !== false,
        phone: fop.phone || "",
        signer: fop.signer || "",
        signer_gen: fop.signer_gen || "",
      });
      // Load signature preview if exists
      if (fop.signature_file_id) {
        const token = localStorage.getItem("token");
        fetch(fopSettingsApi.getSignatureUrl(fop.id), {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((res) => res.blob())
          .then((blob) => setSignaturePreview(URL.createObjectURL(blob)))
          .catch(() => setSignaturePreview(null));
      } else {
        setSignaturePreview(null);
      }
    } else {
      setEditingFop(null);
      setForm({
        name: "",
        full_name: "",
        company_type: "fop",
        country: "Ukraine",
        address: "",
        edrpou: "",
        bank_name: "",
        swift: "",
        bank_accounts: [],
        privatbank_token: "",
        is_active: true,
        phone: "",
        signer: "",
        signer_gen: "",
      });
      setSignaturePreview(null);
    }
    setSignatureFile(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      let fopId = editingFop?.id;
      if (editingFop) {
        await fopSettingsApi.update(editingFop.id, form);
        toast.success("Legal entity updated");
      } else {
        const res = await fopSettingsApi.create(form);
        fopId = res.data?.id;
        toast.success("Legal entity created");
      }
      // Upload signature if a new file was selected
      if (signatureFile && fopId) {
        try {
          await fopSettingsApi.uploadSignature(fopId, signatureFile);
          toast.success("Signature uploaded");
        } catch (err) {
          toast.error("Failed to upload signature");
        }
      }
      setDialogOpen(false);
      fetchFops();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSignatureSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    setSignatureFile(file);
    setSignaturePreview(URL.createObjectURL(file));
  };

  const handleDeleteSignature = async () => {
    if (!editingFop?.id) return;
    try {
      await fopSettingsApi.deleteSignature(editingFop.id);
      setSignaturePreview(null);
      setSignatureFile(null);
      toast.success("Signature deleted");
      fetchFops();
    } catch (err) {
      toast.error("Failed to delete signature");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this legal entity?")) return;
    try {
      await fopSettingsApi.delete(id);
      toast.success("Legal entity deleted");
      fetchFops();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newFops = [...fops];
    [newFops[index], newFops[index - 1]] = [newFops[index - 1], newFops[index]];
    setFops(newFops);
    const order = newFops.map((f, i) => ({ id: f.id, sort_order: i }));
    try {
      await fopSettingsApi.reorder(order);
    } catch (err) {
      fetchFops();
    }
  };

  const handleMoveDown = async (index) => {
    if (index === fops.length - 1) return;
    const newFops = [...fops];
    [newFops[index], newFops[index + 1]] = [newFops[index + 1], newFops[index]];
    setFops(newFops);
    const order = newFops.map((f, i) => ({ id: f.id, sort_order: i }));
    try {
      await fopSettingsApi.reorder(order);
    } catch (err) {
      fetchFops();
    }
  };

  // Bank accounts management
  const addBankAccount = () => {
    setForm({
      ...form,
      bank_accounts: [
        ...form.bank_accounts,
        { currency: "UAH", iban: "", swift: "", legal_name: "", address: "", correspondent_bank: "", correspondent_account: "", correspondent_swift: "" }
      ]
    });
  };

  const updateBankAccount = (index, field, value) => {
    const updated = [...form.bank_accounts];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, bank_accounts: updated });
  };

  const removeBankAccount = (index) => {
    setForm({
      ...form,
      bank_accounts: form.bank_accounts.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50">Legal Entities</h1>
          <p className="text-zinc-500 text-sm mt-1">Manage company legal entities and bank accounts for payments</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" />
          Add Entity
        </Button>
      </div>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-emerald-500" />
            Legal Entities List
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {fops.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <CreditCard className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No legal entities configured</p>
              <p className="text-xs text-zinc-600 mt-1">Add your first legal entity to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="w-20 text-zinc-400 font-medium">Order</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Name</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Type</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Country</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Bank Accounts</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fops.map((fop, index) => (
                  <TableRow key={fop.id} className="border-zinc-800 hover:bg-zinc-800/50">
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-5 w-5 text-zinc-500 hover:text-zinc-50"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-5 w-5 text-zinc-500 hover:text-zinc-50"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === fops.length - 1}
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-zinc-50 font-medium">{fop.name}</p>
                        {fop.full_name && (
                          <p className="text-xs text-zinc-500">{fop.full_name}</p>
                        )}
                        {fop.edrpou && (
                          <p className="text-xs text-zinc-600">EDRPOU: {fop.edrpou}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {COMPANY_TYPES.find(t => t.value === fop.company_type)?.label || fop.company_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-zinc-400 text-sm">{fop.country || "Ukraine"}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {fop.bank_accounts?.length > 0 ? (
                          fop.bank_accounts.map((acc, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                              {acc.currency}
                            </Badge>
                          ))
                        ) : fop.iban ? (
                          <Badge variant="outline" className="text-xs bg-zinc-700/50 text-zinc-400">
                            {fop.iban.substring(0, 10)}...
                          </Badge>
                        ) : (
                          <span className="text-zinc-600 text-xs">No accounts</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={fop.is_active ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-zinc-700/50 text-zinc-500"}
                      >
                        {fop.is_active ? "Active" : "Inactive"}
                      </Badge>
                      {fop.signature_file_id && (
                        <Badge variant="outline" className="ml-1 bg-orange-500/10 text-orange-400 border-orange-500/30 text-[10px]">
                          <FileSignature className="h-3 w-3 mr-0.5" />
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-zinc-400 hover:text-orange-400"
                          onClick={() => openDialog(fop)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-zinc-400 hover:text-red-400"
                          onClick={() => handleDelete(fop.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-500" />
              {editingFop ? "Edit Legal Entity" : "Add Legal Entity"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Display Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Dubko, Smart Orange LLC"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Entity Type</Label>
                <Select value={form.company_type} onValueChange={(v) => setForm({ ...form, company_type: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {COMPANY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-zinc-50">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Country</Label>
                <Input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  placeholder="Ukraine"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">EDRPOU / Registration #</Label>
                <Input
                  value={form.edrpou}
                  onChange={(e) => setForm({ ...form, edrpou: e.target.value })}
                  placeholder="3421111876"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Bank Name</Label>
                <Input
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  placeholder="JSC CB PRIVATBANK"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">SWIFT/BIC</Label>
                <Input
                  value={form.swift}
                  onChange={(e) => setForm({ ...form, swift: e.target.value })}
                  placeholder="PBANUA2X"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>

            {/* Phone & Signer fields */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Телефон</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+380..."
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Підписант (ПІБ)</Label>
                <Input
                  value={form.signer}
                  onChange={(e) => setForm({ ...form, signer: e.target.value })}
                  placeholder="Іванов І.І."
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Підписант (родовий відмінок)</Label>
                <Input
                  value={form.signer_gen}
                  onChange={(e) => setForm({ ...form, signer_gen: e.target.value })}
                  placeholder="Іванова І.І."
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>

            {/* Signature Upload */}
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <Label className="text-zinc-300 flex items-center gap-2 mb-3">
                <FileSignature className="h-4 w-4 text-orange-400" />
                Подпись (PNG)
              </Label>
              <div className="flex items-start gap-4">
                {signaturePreview ? (
                  <div className="relative bg-white rounded-lg p-2 border border-zinc-600">
                    <img
                      src={signaturePreview}
                      alt="Signature"
                      className="h-16 object-contain"
                      data-testid="signature-preview"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute -top-2 -right-2 h-5 w-5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0"
                      onClick={handleDeleteSignature}
                      data-testid="delete-signature-btn"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : null}
                <div>
                  <label htmlFor="signature-upload" className="cursor-pointer">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-zinc-600 hover:border-orange-400 transition-colors text-sm text-zinc-400 hover:text-orange-400">
                      <Upload className="h-4 w-4" />
                      {signaturePreview ? "Заменить" : "Загрузить подпись"}
                    </div>
                  </label>
                  <input
                    id="signature-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleSignatureSelect}
                    data-testid="signature-upload-input"
                  />
                  <p className="text-xs text-zinc-600 mt-1">PNG с прозрачным фоном</p>
                </div>
              </div>
            </div>

            {/* Bank Accounts Section */}
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-blue-400" />
                  Bank Accounts by Currency
                </Label>
                <Button size="sm" variant="outline" onClick={addBankAccount} className="text-blue-400 border-blue-500/30">
                  <Plus className="h-3 w-3 mr-1" />
                  Add Account
                </Button>
              </div>

              {form.bank_accounts.length === 0 ? (
                <p className="text-xs text-zinc-600 text-center py-4">No bank accounts configured. Add accounts for different currencies.</p>
              ) : (
                <div className="space-y-3">
                  {form.bank_accounts.map((acc, idx) => (
                    <div key={idx} className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge className="bg-blue-500/20 text-blue-400">{acc.currency || "Currency"}</Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                          onClick={() => removeBankAccount(idx)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      {/* Legal Name & Address for this currency */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-zinc-500 text-xs">Legal Name ({acc.currency})</Label>
                          <Input
                            value={acc.legal_name || ''}
                            onChange={(e) => updateBankAccount(idx, "legal_name", e.target.value)}
                            placeholder={acc.currency === 'UAH' ? "ФОП Дубко Андрій" : "DUBKO ANDRII"}
                            className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-xs">Address ({acc.currency})</Label>
                          <Input
                            value={acc.address || ''}
                            onChange={(e) => updateBankAccount(idx, "address", e.target.value)}
                            placeholder={acc.currency === 'UAH' ? "м. Київ, вул. Грушевського 1Д" : "1D Hrushevskoho Str., Kyiv"}
                            className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-zinc-500 text-xs">Currency</Label>
                          <Select value={acc.currency} onValueChange={(v) => updateBankAccount(idx, "currency", v)}>
                            <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {CURRENCIES.map(c => (
                                <SelectItem key={c} value={c} className="text-zinc-50">{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-zinc-500 text-xs">{form.country === 'USA' ? 'Account Number' : 'IBAN'}</Label>
                          <Input
                            value={form.country === 'USA' ? (acc.account_number || '') : acc.iban}
                            onChange={(e) => updateBankAccount(idx, form.country === 'USA' ? "account_number" : "iban", e.target.value)}
                            placeholder={form.country === 'USA' ? "898153077008" : "UA233052990000026004005016009"}
                            className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                          />
                        </div>
                      </div>

                      {/* Routing numbers for US accounts */}
                      {form.country === 'USA' && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-zinc-500 text-xs">Routing # (ACH/Paper)</Label>
                            <Input
                              value={acc.routing_number || ''}
                              onChange={(e) => updateBankAccount(idx, "routing_number", e.target.value)}
                              placeholder="063100277"
                              className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-500 text-xs">Routing # (Wire)</Label>
                            <Input
                              value={acc.routing_wire || ''}
                              onChange={(e) => updateBankAccount(idx, "routing_wire", e.target.value)}
                              placeholder="026009593"
                              className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                            />
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-zinc-500 text-xs">SWIFT</Label>
                          <Input
                            value={acc.swift}
                            onChange={(e) => updateBankAccount(idx, "swift", e.target.value)}
                            placeholder="PBANUA2X"
                            className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-zinc-500 text-xs">Correspondent Bank</Label>
                          <Input
                            value={acc.correspondent_bank}
                            onChange={(e) => updateBankAccount(idx, "correspondent_bank", e.target.value)}
                            placeholder="JP Morgan Chase Bank, New York"
                            className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-zinc-500 text-xs">Corresp. Account</Label>
                          <Input
                            value={acc.correspondent_account}
                            onChange={(e) => updateBankAccount(idx, "correspondent_account", e.target.value)}
                            placeholder="001-1-000080"
                            className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-xs">Corresp. SWIFT</Label>
                          <Input
                            value={acc.correspondent_swift}
                            onChange={(e) => updateBankAccount(idx, "correspondent_swift", e.target.value)}
                            placeholder="CHASUS33"
                            className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* PrivatBank API Integration */}
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <Label className="text-zinc-300 flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-green-400" />
                PrivatBank API Integration
              </Label>
              <div className="space-y-2">
                <Label className="text-zinc-500 text-xs">API Token (from Приват24 для бізнесу → Автоклієнт)</Label>
                <Input
                  type="password"
                  value={form.privatbank_token}
                  onChange={(e) => setForm({ ...form, privatbank_token: e.target.value })}
                  placeholder="Enter PrivatBank API token"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 font-mono"
                />
                <p className="text-xs text-zinc-600">
                  Token enables automatic sync of bank transactions. Get it from Приват24 → Автоклієнт → Налаштування API.
                </p>
              </div>
            </div>

            {/* Active checkbox */}
            <div className="flex items-center gap-2 pt-2">
              <Checkbox
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label htmlFor="is_active" className="text-zinc-400 cursor-pointer">
                Active (available for selection in payments)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? "Saving..." : editingFop ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FopSettingsPage;
